import { useCallback, useEffect, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { $getRoot, $getSelection, $isRangeSelection } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { VariableNode, $createVariableNode } from "./VariableNode";
import ToolbarPlugin from "./ToolbarPlugin";
import FloatingLinkEditor from "./FloatingLinkEditor";
import VariablePlugin from "./VariablePlugin";

const EDITOR_THEME = {
  paragraph: "lexical-paragraph",
  text: {
    bold: "lexical-text-bold",
    italic: "lexical-text-italic",
    underline: "lexical-text-underline",
  },
  list: {
    ol: "lexical-list-ol",
    ul: "lexical-list-ul",
    listitem: "lexical-list-item",
  },
  link: "lexical-link",
};


const InsertVariablePlugin = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleInsert = (e) => {
      const variable = e.detail;
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = $createVariableNode(
            variable.name,
            variable.display_name || variable.name
          );
          selection.insertNodes([node]);
        }
      });
    };

    window.addEventListener("insert-email-variable", handleInsert);
    return () =>
      window.removeEventListener("insert-email-variable", handleInsert);
  }, [editor]);

  return null;
};

const LexicalEditor = ({
  initialContent,
  variables,
  onChange,
  onInit,
  onTextChange,
}) => {
  const editorRef = useRef(null);

  const initialConfig = {
    namespace: "EmailTemplateEditor",
    theme: EDITOR_THEME,
    nodes: [ListNode, ListItemNode, LinkNode, AutoLinkNode, VariableNode],
    onError: (error) => {
      console.error("Lexical error:", error);
    },
  };

  const isFirstChange = useRef(true);

  const handleChange = useCallback(
    (editorState, editor) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        // The first change is fired when VariablePlugin initializes the
        // editor from the saved template. Sync the normalized HTML up via
        // onInit (without marking the form dirty) so an unedited body is
        // sent to the backend as block-delimited HTML rather than the raw
        // plain-text default — otherwise newlines collapse in the email.
        if (isFirstChange.current) {
          isFirstChange.current = false;
          if (onInit) {
            onInit(html);
          }
        } else {
          onChange(html);
        }
        // Also report plain text length for character count
        const root = $getRoot();
        const textContent = root.getTextContent();
        if (onTextChange) {
          onTextChange(textContent);
        }
      });
    },
    [onChange, onInit, onTextChange]
  );

  return (
    <div className="lexical-editor-container">
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div className="lexical-editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="lexical-content-editable"
                ref={editorRef}
              />
            }
            placeholder={
              <div className="lexical-placeholder">
                Write your email content...
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <FloatingLinkEditor />
        <VariablePlugin
          initialContent={initialContent}
          variables={variables}
        />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        <InsertVariablePlugin />
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditor;
